import { type AiBuilderChatCopy } from '../utils/aiBuilderChatCopy';
import { type AiBuilderMessagePatchEvent } from '../utils/types';

import { type AiBuilderChoiceOption, type AiBuilderMessage } from './types';

export const getActiveChoiceMessage = (
  messages: AiBuilderMessage[],
): Extract<AiBuilderMessage, { kind: 'choice' }> | null => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];

    if (message?.kind === 'choice' && !message.submitted) {
      return message;
    }
  }

  return null;
};

export const getActiveConfirmMessage = (
  messages: AiBuilderMessage[],
): Extract<AiBuilderMessage, { kind: 'confirm' }> | null => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];

    if (message?.kind === 'confirm' && !message.submitted) {
      return message;
    }
  }

  return null;
};

export const getActiveSelectedChoiceMessage = (
  messages: AiBuilderMessage[],
): Extract<AiBuilderMessage, { kind: 'choice' }> | null => {
  const activeChoiceMessage = getActiveChoiceMessage(messages);

  if (
    !activeChoiceMessage ||
    activeChoiceMessage.selectedOptionIds.length === 0
  ) {
    return null;
  }

  return activeChoiceMessage;
};

export const applyAiBuilderMessageEvent = (
  messages: AiBuilderMessage[],
  event: AiBuilderMessagePatchEvent,
): AiBuilderMessage[] => {
  if (event.type === 'append') {
    return [...messages, event.message];
  }

  return messages.map(message =>
    message.id === event.messageId ? event.message : message,
  );
};

export const getChoiceOptionLabels = (
  options: AiBuilderChoiceOption[],
  selectedOptionIds: string[],
) => {
  const selectedOptionIdSet = new Set(selectedOptionIds);
  return options
    .filter(option => selectedOptionIdSet.has(option.id))
    .map(option => option.label);
};

export const composeChoiceMessageContent = ({
  selectedLabels,
  responseText,
}: {
  selectedLabels: string[];
  responseText?: string;
}) => {
  return [selectedLabels.join(', '), responseText?.trim()]
    .filter(Boolean)
    .join('\n');
};

export const stopRunningProgressMessages = ({
  copy,
  messages,
  stoppedMessage,
}: {
  copy: AiBuilderChatCopy;
  messages: AiBuilderMessage[];
  stoppedMessage: Extract<AiBuilderMessage, { kind: 'assistant' }>;
}): AiBuilderMessage[] => {
  return [
    ...messages.map(message => {
      if (message.kind !== 'progress' || message.status !== 'running') {
        return message;
      }

      return {
        ...message,
        status: 'stopped' as const,
        title: copy.system.progressStopped,
        // ephemeral 한 사고 과정은 중단된 카드에 남기지 않는다.
        thinkingText: undefined,
        steps: message.steps.map(step =>
          step.status !== 'done'
            ? { ...step, status: 'stopped' as const }
            : step,
        ),
      };
    }),
    stoppedMessage,
  ];
};

export const updateChoiceOptionSelection = ({
  messages,
  messageId,
  optionId,
}: {
  messages: AiBuilderMessage[];
  messageId: string;
  optionId: string;
}): AiBuilderMessage[] => {
  return messages.map(message => {
    if (
      message.id !== messageId ||
      message.kind !== 'choice' ||
      message.submitted
    ) {
      return message;
    }

    const selectedOptionIdSet = new Set(message.selectedOptionIds);

    if (message.selectionMode === 'single') {
      return {
        ...message,
        selectedOptionIds: selectedOptionIdSet.has(optionId) ? [] : [optionId],
      };
    }

    if (selectedOptionIdSet.has(optionId)) {
      selectedOptionIdSet.delete(optionId);
    } else {
      selectedOptionIdSet.add(optionId);
    }

    return {
      ...message,
      selectedOptionIds: [...selectedOptionIdSet],
    };
  });
};

export const createGenerationCompleteMessage = ({
  copy,
  requestId,
  selectedLabels,
  quizCount,
}: {
  copy: AiBuilderChatCopy;
  requestId: number;
  selectedLabels: string[];
  quizCount: number;
}): Extract<AiBuilderMessage, { kind: 'assistant' }> => {
  return {
    id: `ai-builder-${requestId}-generation-complete`,
    kind: 'assistant',
    content: copy.legacyGenerationComplete.content({
      selectedLabels,
      quizCount,
    }),
    jumpLinks: Array.from({ length: quizCount }, (_, index) => ({
      quizNumber: index + 1,
      label: copy.serverEvent.questionLabel(index + 1),
    })),
    actions: [
      {
        id: 'undo-last-ai-action',
        label: copy.action.undoLastAiAction,
        type: 'undoLastAiAction',
        prompt: copy.action.undoLastAiAction,
      },
      {
        id: 'complete-generation',
        label: copy.action.complete,
        type: 'closePanel',
      },
    ],
  };
};
